// src/components/player/PlayerOverviewTab.js - REDESIGNED: B1 Final Layout
// Layout: Full-width stat table + Triptych (Rate Profile | MLB Rankings | League Rankings)
// Row colors: Season = white, 14 Day Rolling = yellow (#eab308), Projected = light blue (#60a5fa) bold
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../services/colorService';
import api from '../../services/apiService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { AlertCircle } from 'lucide-react';
import ProjectionEngineSwitcher, { ENGINES } from './ProjectionEngineSwitcher';
import { useProjectionEngine } from '../../hooks/useProjectionEngine';
import ProjectionInsights from './ProjectionInsights';


// Scope toggle component
// Chunk 3.11.b post-regen: ONE shared TOGGLE_BTN style for every toggle group
// (Year + Position scope + Cats filter). Same height, padding, corner radius,
// font, and active/inactive colors. Width sizes to content via consistent
// horizontal padding — sibling component family, visually uniform without
// being rigid-equal-width.
const TOGGLE_BTN = {
  border: 'none', borderRadius: 4,
  padding: '2px 8px',
  fontSize: 10, fontWeight: 600,
  cursor: 'pointer',
  height: 18, lineHeight: '14px',
  textAlign: 'center', boxSizing: 'border-box',
};
const YearToggle = ({ active, onToggle, currentSeason }) => (
  <div style={{ display: 'inline-flex', gap: 2, marginLeft: 8 }}>
    {[currentSeason - 1, currentSeason].map(yr => (
      <button key={yr} onClick={() => onToggle(yr)}
        style={{ ...TOGGLE_BTN,
          backgroundColor: active === yr ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700], color: active === yr ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
      >{yr}</button>
    ))}
  </div>
);

const ScopeToggle = ({ active, onToggle, positionLabel, leftKey = 'all', leftLabel = 'All', rightKey = 'position' }) => (
  <div style={{ display: 'inline-flex', gap: 2, marginLeft: 8 }}>
    <button onClick={() => onToggle(leftKey)}
      style={{ ...TOGGLE_BTN,
        backgroundColor: active === leftKey ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700], color: active === leftKey ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
    >{leftLabel}</button>
    <button onClick={() => onToggle(rightKey)}
      style={{ ...TOGGLE_BTN,
        backgroundColor: active === rightKey ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700], color: active === rightKey ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
    >{positionLabel ?? 'Pos'}</button>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// INLINE STYLES — Matches mockup B1 final exactly
// ═══════════════════════════════════════════════════════════════
const styles = {
  // Panel container (shared by all sections)
  panel: {
    background: 'rgba(23,23,23,0.5)',
    borderRadius: 5,
    border: '1px solid rgba(55,65,81,0.5)',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '6px 12px 5px',
    borderBottom: '1px solid rgba(55,65,81,0.2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  // Phase 1.2 fix (2026-05-20): two-row panel header for rankings cards.
  // Row 1 = title only; row 2 = control strip that wraps as a single unit.
  // Replaces the prior one-row header where position-buttons could wrap
  // (flexWrap: 'wrap') while year/scope toggle siblings could not, producing
  // mixed stacked/inline layouts that differed between the two cards.
  panelHeaderStack: {
    padding: '6px 12px 5px',
    borderBottom: '1px solid rgba(55,65,81,0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  panelControlStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    rowGap: 4,
  },
  panelTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1.1px',
    textTransform: 'uppercase',
    color: dynastyTheme.tokens.colors.primary,
  },
  panelSub: {
    fontSize: 9,
    color: dynastyTheme.tokens.colors.neutral[700],
  },

  // ─── Stat Table ───
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    fontSize: 11,
    fontWeight: 600,
    color: dynastyTheme.tokens.colors.neutral[600],
    padding: '6px 8px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(55,65,81,0.25)',
  },
  thFirst: {
    textAlign: 'left',
    paddingLeft: 14,
    width: 120,
  },
  td: {
    fontSize: 13,
    fontWeight: 400,
    padding: '5px 8px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(55,65,81,0.08)',
  },
  tdFirst: {
    textAlign: 'left',
    paddingLeft: 14,
    fontSize: 11,
  },
  // Row color variants (design intent, file header): Season = white,
  // 14 Day Rolling = team-primary gold, Projected = light blue (#60a5fa, bold).
  // projected uses the documented design shade (tailwind blue-400) — the `info`
  // token is blue-500 (#3b82f6), one step darker than the intended light blue.
  // The component is inline-style based (see getRateBarColor's design rgba's), so
  // a documented design value is consistent here; it is NOT a Rule-9 fallback.
  seasonColor: dynastyTheme.tokens.colors.white,
  l14Color: dynastyTheme.tokens.colors.primary,
  projectedColor: '#60a5fa',

  // ─── Triptych layout (equal columns) ───
  triptych: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 6,
    marginTop: 6,
  },

  // ─── Rate Profile ───
  rateList: {
    padding: '8px 14px 10px',
  },
  rateItem: {
    display: 'grid',
    gridTemplateColumns: '62px 1fr 58px',
    alignItems: 'center',
    gap: 8,
    padding: '5px 0',
  },
  rateLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: dynastyTheme.tokens.colors.neutral[400],
  },
  rateTrack: {
    height: 7,
    background: 'rgba(55,65,81,0.3)',
    borderRadius: 3,
    overflow: 'hidden',
    position: 'relative',
  },
  rateFill: {
    height: '100%',
    borderRadius: 3,
    position: 'absolute',
    left: 0,
    top: 0,
  },
  rateAvgMarker: {
    position: 'absolute',
    top: -2,
    width: 2,
    height: 11,
    background: 'rgba(255,255,255,0.4)',
    borderRadius: 1,
  },
  rateVal: {
    fontSize: 13,
    fontWeight: 600,
    color: dynastyTheme.tokens.colors.neutral[200],
    textAlign: 'right',
  },
  rateCtx: {
    display: 'block',
    fontSize: 10,
    color: dynastyTheme.tokens.colors.neutral[600],
    fontWeight: 400,
  },

  // ─── Monthly (full-width strip below triptych) ───
  monthlyStrip: {
    marginTop: 6,
  },
  monthlyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4,
    padding: '6px 12px 8px',
  },
  moName: {
    fontSize: 9,
    color: dynastyTheme.tokens.colors.neutral[600],
    fontWeight: 600,
    textTransform: 'uppercase',
  },

  // ─── Rankings ───
  rankOverall: {
    textAlign: 'center',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(55,65,81,0.15)',
  },
  roText: {
    fontSize: 15,
    fontWeight: 600,
    color: dynastyTheme.tokens.colors.neutral[200],
  },
  roSub: {
    fontSize: 10,
    color: dynastyTheme.tokens.colors.neutral[600],
    marginTop: 1,
  },
  rankList: {
    padding: '6px 14px 10px',
  },
  rlRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 0',
    borderBottom: '1px solid rgba(55,65,81,0.08)',
  },
  rlCat: {
    fontSize: 13,
    color: dynastyTheme.tokens.colors.neutral[400],
  },
  rlVal: {
    fontSize: 13,
    fontWeight: 600,
    color: dynastyTheme.tokens.colors.neutral[200],
  },
};

// ═══════════════════════════════════════════════════════════════
// RATE BAR COLOR HELPER
// ═══════════════════════════════════════════════════════════════
const getRateBarColor = (value, avg, higherIsBetter) => {
  if (higherIsBetter) {
    if (value >= avg) return 'rgba(16,185,129,0.6)'; // good - green
    if (value >= avg * 0.8) return 'rgba(245,158,11,0.6)'; // warn - amber
    return 'rgba(239,68,68,0.5)'; // bad - red
  } else {
    // For K% — lower is better
    if (value <= avg) return 'rgba(16,185,129,0.6)';
    if (value <= avg * 1.2) return 'rgba(245,158,11,0.6)';
    return 'rgba(239,68,68,0.5)';
  }
};

// ═══════════════════════════════════════════════════════════════
// MONTHLY HEAT COLOR HELPER
// ═══════════════════════════════════════════════════════════════
const getMonthlyStyle = (value, isPitcher) => {
  if (value == null || value === 0) {
    return { background: 'transparent', color: dynastyTheme.tokens.colors.neutral[800], border: '1px solid rgba(55,65,81,0.2)' };
  }
  if (isPitcher) {
    // ERA: lower is better
    if (value < 3.0) return { background: 'rgba(16,185,129,0.06)', color: dynastyTheme.tokens.colors.success, border: '1px solid rgba(55,65,81,0.2)' };
    if (value < 3.75) return { background: 'rgba(234,179,8,0.05)', color: dynastyTheme.tokens.colors.primary, border: '1px solid rgba(55,65,81,0.2)' };
    if (value < 4.5) return { background: 'rgba(249,115,22,0.05)', color: dynastyTheme.tokens.colors.warning, border: '1px solid rgba(55,65,81,0.2)' };
    return { background: 'rgba(239,68,68,0.05)', color: dynastyTheme.tokens.colors.error, border: '1px solid rgba(55,65,81,0.2)' };
  } else {
    // AVG: higher is better
    if (value > 0.280) return { background: 'rgba(16,185,129,0.06)', color: dynastyTheme.tokens.colors.success, border: '1px solid rgba(55,65,81,0.2)' };
    if (value > 0.250) return { background: 'rgba(234,179,8,0.05)', color: dynastyTheme.tokens.colors.primary, border: '1px solid rgba(55,65,81,0.2)' };
    if (value > 0.220) return { background: 'rgba(249,115,22,0.05)', color: dynastyTheme.tokens.colors.warning, border: '1px solid rgba(55,65,81,0.2)' };
    return { background: 'rgba(239,68,68,0.05)', color: dynastyTheme.tokens.colors.error, border: '1px solid rgba(55,65,81,0.2)' };
  }
};

// ═══════════════════════════════════════════════════════════════
// MONTH NAMES
// ═══════════════════════════════════════════════════════════════
const MONTH_LABELS = ['MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP'];

// ═══════════════════════════════════════════════════════════════
// CATEGORY → STAT KEY MAP (shared by both rankings cards' Scoring filter)
// ═══════════════════════════════════════════════════════════════
// Translates league-scoring-category labels (e.g., 'R', 'HR', 'OPS') to the
// stat keys used in cache rows (e.g., 'runs', 'home_runs', 'ops'). Used by
// the Scoring / All Cats toggle to filter the displayed categories on each
// rankings card to only what the league actually scores.
const CATEGORY_TO_KEY = {
  'r': 'runs', 'hr': 'home_runs', 'rbi': 'rbi', 'sb': 'stolen_bases',
  'avg': 'batting_avg', 'ops': 'ops', 'obp': 'obp', 'slg': 'slg',
  'w': 'wins', 'sv': 'saves', 'era': 'era', 'whip': 'whip',
  'so': 'strikeouts_pitched', 'k': 'strikeouts_pitched', 'qs': 'quality_starts',
};

// ═══════════════════════════════════════════════════════════════
// MLB AVERAGES — fallback only; prefer data.mlb_averages from API
// ═══════════════════════════════════════════════════════════════
// Fallback values use canonical snake_case keys and raw decimal values (matching backend)
const MLB_AVG_FALLBACK = {
  hitter: { k_pct: 0.220, bb_pct: 0.085, iso: 0.150, xbh_pct: 0.030 },
  pitcher: { k_per_9: 8.5, bb_per_9: 3.2, k_bb_ratio: 2.7, hr_per_9: 1.2 },
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const PlayerOverviewTab = ({ player, playerId, leagueId, isPitcher, statsSeasonYear }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mlbScope, setMlbScope] = useState('all');
  const { engine: projectionEngine, selectEngine } = useProjectionEngine();  // Chunk 30 Model Switcher
  const [leagueScope, setLeagueScope] = useState('all');
  const [catFilter, setCatFilter] = useState('league');
  // Phase 1.2 fix (2026-05-20): the two rankings cards must have INDEPENDENT
  // year state. Previously a single `rankingsYear` was shared, so toggling
  // the year on one card flipped the other. Now each card has its own year
  // state, and `rankingsByYear` is a small in-memory cache keyed by year so
  // we never refetch the same year twice within a player view.
  const [mlbRankingsYear, setMlbRankingsYear] = useState(statsSeasonYear);
  const [leagueRankingsYear, setLeagueRankingsYear] = useState(statsSeasonYear);
  const [rankingsByYear, setRankingsByYear] = useState({});
  const [rankingsLoadingYears, setRankingsLoadingYears] = useState(() => new Set());
  const playerPosition = player?.info?.position;
  const leagueScoring = player?.analytics?.league_scoring;

  const currentSeason = statsSeasonYear;

  // ─── Fetch overview data ───
  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;

    const fetchOverviewData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`📊 [Overview Tab] Fetching overview data for player ${playerId}`,
          leagueId ? `in league ${leagueId}` : 'globally');

        const response = await api.players.getPlayerOverview(playerId, leagueId, currentSeason);
        if (!cancelled) {
          setData(response);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('❌ [Overview Tab] Error:', err);
          setError(err.message || 'Failed to load overview data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOverviewData();

    return () => {
      cancelled = true;
    };
  }, [playerId, leagueId, currentSeason]);

  // ─── Fetch rankings for any toggled year not yet cached ───
  // Per-card year toggles share the same cache: if MLB card is on 2025 and
  // League card later toggles to 2025, the fetch is reused.
  // Chunk 3.11.a #4: track loading explicitly per year; never fall back to
  // a different year's data under the selected-year label (Rule 9).
  // Phase 1.2 (2026-05-20): each year-toggle fetches independently of the
  // other card.
  useEffect(() => {
    if (!playerId) return;
    const yearsNeeded = new Set();
    if (mlbRankingsYear && mlbRankingsYear !== currentSeason) {
      yearsNeeded.add(mlbRankingsYear);
    }
    if (leagueRankingsYear && leagueRankingsYear !== currentSeason) {
      yearsNeeded.add(leagueRankingsYear);
    }
    if (yearsNeeded.size === 0) return;

    let cancelled = false;
    const toFetch = Array.from(yearsNeeded).filter(
      (yr) => rankingsByYear[yr] === undefined && !rankingsLoadingYears.has(yr)
    );
    if (toFetch.length === 0) return;

    setRankingsLoadingYears((prev) => {
      const next = new Set(prev);
      toFetch.forEach((yr) => next.add(yr));
      return next;
    });

    toFetch.forEach(async (yr) => {
      try {
        const resp = await api.players.getPlayerOverview(playerId, leagueId, yr);
        if (cancelled) return;
        setRankingsByYear((prev) => ({ ...prev, [yr]: resp?.rankings || null }));
      } catch (e) {
        console.error(`Rankings ${yr} fetch error:`, e);
        if (cancelled) return;
        setRankingsByYear((prev) => ({ ...prev, [yr]: null }));
      } finally {
        if (cancelled) return;
        setRankingsLoadingYears((prev) => {
          const next = new Set(prev);
          next.delete(yr);
          return next;
        });
      }
    });

    return () => { cancelled = true; };
  }, [playerId, leagueId, mlbRankingsYear, leagueRankingsYear, currentSeason]);

  // ─── Loading / Error / Empty states ───
  if (loading) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
        <div className={dynastyTheme.classes.text.primary}>Loading overview...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
        <AlertCircle className={`w-8 h-8 ${dynastyTheme.classes.text.error} mx-auto mb-2`} />
        <div className={dynastyTheme.classes.text.error}>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  // ─── Extract data ───
  const seasonStats = player?.stats?.season || {};
  const rollingStats = player?.stats?.rolling_14_day || {};
  const hitting = seasonStats.hitting || {};
  const pitching = seasonStats.pitching || {};
  const rollingHitting = rollingStats.hitting || {};
  const rollingPitching = rollingStats.pitching || {};
  // Chunk 3.11.a #4 + Phase 1.2 (2026-05-20): per-card rankings binding.
  // Each card resolves its OWN year (mlbRankingsYear vs leagueRankingsYear)
  // against either the main-fetch payload (when year === currentSeason) or
  // the per-year cache. NEVER fall back to a different year's data under the
  // selected-year label (Rule 9 — skill_30d-class defect).
  const mlbRankings = mlbRankingsYear === currentSeason
    ? (data?.rankings || null)
    : (rankingsByYear[mlbRankingsYear] ?? null);
  const leagueRankings = leagueRankingsYear === currentSeason
    ? (data?.rankings || null)
    : (rankingsByYear[leagueRankingsYear] ?? null);
  const mlbRankingsLoading = mlbRankingsYear !== currentSeason
    && rankingsLoadingYears.has(mlbRankingsYear);
  const leagueRankingsLoading = leagueRankingsYear !== currentSeason
    && rankingsLoadingYears.has(leagueRankingsYear);
  const monthly = data.monthly || [];

  // ═══════════════════════════════════════════════════════════
  // CC INSTRUCTION: Projected stats come from the z-score cache table.
  // Wire this up by fetching from the projections endpoint or pulling
  // from the player's projection data in the z-score cache.
  // For now, fall back to empty object — the row will show "—" for each cell.
  // ═══════════════════════════════════════════════════════════
  // V1: Projections now come as full canonical stats_snapshot
  // Chunk 30 Model Switcher: select the engine's projection block (consensus default),
  // falling back to the canonical consensus projections when per-engine isn't present.
  const projSnapshot = data.projected || {};
  const projGroup = isPitcher ? projSnapshot?.stats_data?.pitching : projSnapshot?.stats_data?.hitting;
  const byEngine = projGroup?.projections_by_engine;
  // §9 field semantics — each labeled number reads its matching field, NO cross-field fallback:
  //   full-season (consensus/default) → projected_full (updated full-season = current + ROS)
  //   per-engine switch (PS/CF)        → projections_by_engine[engine]  (TALENT, shown as such)
  //   rest-of-season column            → projections_ros                (below, Rule-9 explicit)
  // A missing projected_full renders "—" (proj() returns null on the empty default), so a broken
  // daily refresh is VISIBLE — it is never masked by silently substituting the talent total.
  // (byEngine is OMITTED, not consensus-collapsed, when per-engine substrate is absent — Bug 2 —
  // so when byEngine exists every engine key is real and the switcher shows; otherwise it stays
  // hidden at :623 and the consensus full-season line is shown.)
  const projWholeSeason = (byEngine && projectionEngine && projectionEngine !== 'consensus')
    ? (byEngine[projectionEngine] || {})
    : (projGroup?.projected_full || {});
  const projectedStats = projWholeSeason;  // whole-season row accessor (proj()/getProjectedVal)
  // Rest-of-season (Fork B) consumed EXPLICITLY. Rule 9: NO `projections_ros || projections`
  // short-circuit — ROS absent while whole-season present is a contract violation surfaced
  // honestly in the render (the serving layer fires the RosContractViolations canary).
  // Bug 5: ROS is per-engine when a non-consensus engine is selected, so current/ROS/season-total
  // reconcile on EVERY engine view (the per-engine season-total is the blend of current and the
  // engine's ROS; showing the consensus ROS against a per-engine total looked impossible -- CF OPS
  // season .689 below the consensus ROS .870). Consensus view keeps the canonical projections_ros.
  const projRos = (byEngine && projectionEngine && projectionEngine !== 'consensus')
    ? (projGroup?.projections_ros_by_engine?.[projectionEngine] ?? null)
    : (projGroup?.projections_ros ?? null);
  // DD-B4 (Tony's law — one product, one number, 2026-07-14): the ROS OPS HEADLINE is P9's
  // PUBLISHED calibrated forward OPS (published_forward_ops.ops — the SAME field P9's page
  // renders; Valdez .835, Chapman .800), NOT the per-engine PS view (.748) and NOT
  // consensus-if-preference. DD consumes P9; they read one field so they cannot diverge.
  // Graceful fallback BEFORE the first materializing compose run (or for anchor-less hitters):
  // the consensus forward fwd_p50 already in projections_ros.ops (≈ published within ~.001),
  // so DD is never wrong even pre-materialization. The persisted engine preference still drives
  // the OTHER cells / per-engine breakdown; only the OPS headline is pinned to the published #.
  const publishedForwardOps = (!isPitcher && projGroup?.published_forward_ops?.ops != null)
    ? projGroup.published_forward_ops.ops
    : (projGroup?.projections_ros?.ops ?? null);
  const engineLabel = ((ENGINES || []).find(e => e.key === projectionEngine) || {}).label || projectionEngine;
  const engineSuffix = (byEngine && projectionEngine && projectionEngine !== 'consensus')
    ? ` — ${engineLabel}` : '';
  // Per-engine substrate (PS vs CF). Agreement is judged ONE way — see enginesAgreeOnCol
  // (defined after `columns`) — and that single predicate drives BOTH the "=" markers AND the
  // disagree chip, so they can never contradict. (Prior bug: the chip used a raw all-keys 10%
  // test including NON-displayed stats like HR-allowed, while the markers used displayed-value
  // equality — opposite answers, so the amber chip lit while "=" blanketed the card.)
  const _psB = byEngine?.pattern_spotter || {};
  const _cfB = byEngine?.cautious_forecaster || {};

  // V2: MLB averages now come as canonical snake_case raw decimals
  const MLB_AVG = {
    hitter: data.mlb_averages?.hitter || MLB_AVG_FALLBACK.hitter,
    pitcher: data.mlb_averages?.pitcher || MLB_AVG_FALLBACK.pitcher,
  };

  // ─── Helpers to safely extract stats ───
  const h = (key, fallback = 0) => hitting[key] ?? fallback;
  const p = (key, fallback = 0) => pitching[key] ?? fallback;
  const rh = (key, fallback = 0) => rollingHitting[key] ?? fallback;
  const rp = (key, fallback = 0) => rollingPitching[key] ?? fallback;
  const proj = (key) => projectedStats[key] ?? null;

  // ─── Compute rate stats ───
  const pa = h('plate_appearances') || 1;
  const ab = h('at_bats') || 1;
  const ip = p('innings_pitched') || 1;
  // DD-MODAL P5 fix (2026-07-15): honest empty-state. A player with no
  // current-season stats has PA/AB/IP of 0; the `|| 1` denominators above would
  // otherwise render fabricated 0.0%/.000 rate bars as if real. Gate the panel on
  // real volume so a statless player sees an honest "no data" instead of zeros.
  const hasRateStats = isPitcher
    ? (p('innings_pitched') > 0)
    : (h('plate_appearances') > 0 || h('at_bats') > 0);

  // V2: MLB averages are now raw decimals — derive display values in frontend
  const hitterAvgKPct = (MLB_AVG.hitter.k_pct || 0) * 100;
  const hitterAvgBbPct = (MLB_AVG.hitter.bb_pct || 0) * 100;
  const hitterAvgContactPct = (1 - (MLB_AVG.hitter.k_pct || 0)) * 100;
  const hitterAvgBbK = (MLB_AVG.hitter.bb_pct || 0) / (MLB_AVG.hitter.k_pct || 1);

  let rateStats;
  if (isPitcher) {
    rateStats = [
      { label: 'K/9', value: (p('strikeouts_pitched') / ip * 9).toFixed(1), avg: MLB_AVG.pitcher.k_per_9, pct: (p('strikeouts_pitched') / ip * 9) / 15 * 100, avgPct: (MLB_AVG.pitcher.k_per_9 || 0) / 15 * 100, higherIsBetter: true },
      { label: 'BB/9', value: (p('walks_allowed') / ip * 9).toFixed(1), avg: MLB_AVG.pitcher.bb_per_9, pct: (p('walks_allowed') / ip * 9) / 8 * 100, avgPct: (MLB_AVG.pitcher.bb_per_9 || 0) / 8 * 100, higherIsBetter: false },
      { label: 'K/BB', value: (p('strikeouts_pitched') / Math.max(p('walks_allowed'), 1)).toFixed(2), avg: MLB_AVG.pitcher.k_bb_ratio, pct: Math.min((p('strikeouts_pitched') / Math.max(p('walks_allowed'), 1)) / 6 * 100, 100), avgPct: (MLB_AVG.pitcher.k_bb_ratio || 0) / 6 * 100, higherIsBetter: true },
      { label: 'HR/9', value: (p('home_runs_allowed', 0) / ip * 9).toFixed(1), avg: MLB_AVG.pitcher.hr_per_9, pct: (p('home_runs_allowed', 0) / ip * 9) / 3 * 100, avgPct: (MLB_AVG.pitcher.hr_per_9 || 0) / 3 * 100, higherIsBetter: false },
      { label: 'WHIP', value: ((p('walks_allowed') + p('hits_allowed', 0)) / ip).toFixed(2), avg: 1.25, pct: Math.min(((p('walks_allowed') + p('hits_allowed', 0)) / ip) / 2.5 * 100, 100), avgPct: 1.25 / 2.5 * 100, higherIsBetter: false },
      { label: 'GB%', value: p('ground_balls_pct') ? (p('ground_balls_pct') * 100).toFixed(1) + '%' : '—', avg: 43, pct: (p('ground_balls_pct', 0.43) * 100), avgPct: 43, higherIsBetter: true },
    ];
  } else {
    const kPct = (h('strikeouts') / pa) * 100;
    const bbPct = (h('walks') / pa) * 100;
    const iso = (h('slg') || 0) - (h('batting_avg') || 0);
    const hrAb = h('home_runs') / ab;
    const contactPct = 100 - kPct;
    const bbK = h('walks') / Math.max(h('strikeouts'), 1);

    rateStats = [
      { label: 'K%', value: kPct.toFixed(1) + '%', avg: hitterAvgKPct, pct: Math.min(kPct / 40 * 100, 100), avgPct: hitterAvgKPct / 40 * 100, higherIsBetter: false, rawValue: kPct },
      { label: 'BB%', value: bbPct.toFixed(1) + '%', avg: hitterAvgBbPct, pct: Math.min(bbPct / 20 * 100, 100), avgPct: hitterAvgBbPct / 20 * 100, higherIsBetter: true, rawValue: bbPct },
      { label: 'ISO', value: iso.toFixed(3), avg: MLB_AVG.hitter.iso, pct: Math.min(iso / 0.350 * 100, 100), avgPct: (MLB_AVG.hitter.iso || 0) / 0.350 * 100, higherIsBetter: true, rawValue: iso },
      { label: 'HR/AB', value: hrAb.toFixed(3), avg: MLB_AVG.hitter.xbh_pct, pct: Math.min(hrAb / 0.070 * 100, 100), avgPct: (MLB_AVG.hitter.xbh_pct || 0) / 0.070 * 100, higherIsBetter: true, rawValue: hrAb },
      { label: 'Contact%', value: contactPct.toFixed(1) + '%', avg: hitterAvgContactPct, pct: Math.min(contactPct / 100 * 100, 100), avgPct: hitterAvgContactPct / 100 * 100, higherIsBetter: true, rawValue: contactPct },
      { label: 'BB/K', value: bbK.toFixed(2), avg: hitterAvgBbK, pct: Math.min(bbK / 1.0 * 100, 100), avgPct: hitterAvgBbK / 1.0 * 100, higherIsBetter: true, rawValue: bbK },
    ];
  }

  // ─── Build table columns based on hitter vs pitcher ───
  const columns = isPitcher
    ? [
        { key: 'games_played', label: 'G' },
        { key: 'games_started', label: 'GS' },
        { key: 'innings_pitched', label: 'IP', format: v => v?.toFixed(1) ?? '—' },
        { key: 'wins', label: 'W' },
        { key: 'losses', label: 'L' },
        { key: 'saves', label: 'S' },
        { key: 'era', label: 'ERA', format: v => formatERA(v) },
        { key: 'whip', label: 'WHIP', format: v => v?.toFixed(2) ?? '—' },
        { key: 'strikeouts_pitched', label: 'K' },
        { key: 'quality_starts', label: 'QS' },
      ]
    : [
        { key: 'games_played', label: 'G' },
        { key: 'at_bats', label: 'AB' },
        { key: 'hits', label: 'H' },
        { key: 'walks', label: 'BB' },
        { key: 'runs', label: 'R' },
        { key: 'stolen_bases', label: 'SB' },
        { key: 'home_runs', label: 'HR' },
        { key: 'rbi', label: 'RBI' },
        { key: 'batting_avg', label: 'AVG', format: v => formatBattingAvg(v) },
        { key: 'ops', label: 'OPS', format: v => formatBattingAvg(v) },
      ];

  // ─── Stat value extraction per row ───
  const getSeasonVal = (col) => {
    const src = isPitcher ? pitching : hitting;
    const raw = src[col.key];
    if (raw == null) return '—';
    return col.format ? col.format(raw) : raw;
  };

  const getRollingVal = (col) => {
    const src = isPitcher ? rollingPitching : rollingHitting;
    const raw = src[col.key];
    if (raw == null) return '—';
    return col.format ? col.format(raw) : raw;
  };

  const getProjectedVal = (col) => {
    const raw = proj(col.key);
    if (raw == null) return '—';
    return col.format ? col.format(raw) : raw;
  };

  // Rest-of-season value — reads projRos explicitly (no fallback to whole-season).
  // DD-B4: the OPS cell is PINNED to P9's published forward OPS (one product, one number),
  // regardless of the selected engine; every other cell follows projRos / the engine view.
  const getRosVal = (col) => {
    if (!isPitcher && col.key === 'ops' && publishedForwardOps != null) {
      return col.format ? col.format(publishedForwardOps) : publishedForwardOps;
    }
    const raw = projRos ? projRos[col.key] : null;
    if (raw == null) return '—';
    return col.format ? col.format(raw) : raw;
  };

  // ─── Category rank display names ───
  const RANK_DISPLAY_NAMES = {
    batting_avg: 'Batting AVG',
    ops: 'OPS',
    home_runs: 'Home Runs',
    rbi: 'RBI',
    runs: 'Runs',
    stolen_bases: 'Stolen Bases',
    era: 'ERA',
    whip: 'WHIP',
    wins: 'Wins',
    strikeouts_pitched: 'Strikeouts',
    quality_starts: 'Quality Starts',
    saves: 'Saves',
  };

  const gamesPlayed = isPitcher ? (p('games_played') || 0) : (h('games_played') || 0);

  // Bug #2 (honest, DD display): make engine AGREEMENT legible — never fabricate
  // disagreement. Engines "agree" on a stat when their DISPLAYED values are identical:
  // either single-model by Bible §4.145 (IP/W/QS/GS/SV/HLD) OR genuinely two-engine values
  // that round to the same displayed number (e.g. K 75.4 vs 75.1 → "75"). Compared on the
  // FORMATTED value, so sub-display-grain differences honestly read as agreement (no
  // manufactured difference). The two-engine family that DOES differ keeps toggling.
  const _fmtCol = (col, val) => {
    if (val == null) return '—';                       // mirror cell extraction exactly
    return col?.format ? col.format(val) : val;        // (don't call formatERA on null)
  };
  // ONE agreement predicate (drives both the "=" markers and the disagree chip). Engines AGREE
  // on a displayed stat unless PS vs CF differ BOTH visibly (the formatted/displayed value
  // differs) AND materially. Single-model stats (IP/W/QS/GS/SV/HLD) and stats absent for an
  // engine read as agreement.
  //
  // DEFECT-1b FIX (2026-07-14, Valdez 699013): the tolerance ACTUALLY compares the engine
  // numbers now. The old `Math.max(|a|,|b|,1) * 0.10` degenerated to a 0.10 ABSOLUTE tolerance
  // for EVERY sub-1.0 rate (the `1` floor dominated), so a 90-point OPS split (.748 vs .842)
  // read as "engines agree" — the chip ASSERTED agreement instead of computing it. Rate stats
  // now use an ABSOLUTE, stat-scaled tolerance (a real engine split exceeds it); count stats use
  // a pure relative tolerance with NO `1` floor. A 90-pt OPS gap can never read as agreement.
  const AGREE_TOL = 0.10;   // relative tolerance — COUNT stats only
  const AGREE_ABS_TOL = {   // absolute tolerance — RATE stats (≈ a small fraction of the stat's spread)
    batting_avg: 0.015, obp: 0.015, slg: 0.020, ops: 0.020,
    era: 0.35, whip: 0.07, k_per_9: 0.8, bb_per_9: 0.5, hr_per_9: 0.3, fip: 0.35,
  };
  const enginesAgreeOnCol = (col) => {
    if (!byEngine) return true;
    const a = Number(_psB[col.key]);
    const b = Number(_cfB[col.key]);
    if (!isFinite(a) || !isFinite(b)) return true;        // single-model / stat absent → agree
    if (_fmtCol(col, a) === _fmtCol(col, b)) return true;  // identical on the DISPLAYED value
    const absTol = AGREE_ABS_TOL[col.key];
    if (absTol != null) return Math.abs(a - b) <= absTol;  // rate: absolute, stat-scaled (no 1-floor)
    return Math.abs(a - b) <= Math.max(Math.abs(a), Math.abs(b)) * AGREE_TOL; // count: pure relative
  };
  const allEnginesAgree = !!byEngine && columns.every(enginesAgreeOnCol);
  // The chip is the exact complement of all-agree on the SAME predicate — it can never
  // contradict the per-stat markers (amber ⟺ some displayed stat genuinely differs ⟺ a cell
  // without "="; green ⟺ every displayed stat agrees ⟺ markers suppressed).
  const enginesDisagree = !!byEngine && !allEnginesAgree;
  // Per-stat "=" marker: shown ONLY in the mixed case (some stats differ) — it tells the
  // user which displayed stats the engines land on together, so an unchanged value reads
  // as agreement, not a dead toggle. When ALL agree, the card-level chip says so instead.
  const agreeMark = (col) => (byEngine && !allEnginesAgree && enginesAgreeOnCol(col)) ? (
    <span title="Both engines land on this number — no disagreement here"
      style={{ marginLeft: 3, fontSize: 9, verticalAlign: 'super', cursor: 'help', color: dynastyTheme.tokens.colors.neutral[500] }}>=</span>
  ) : null;

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div style={{ padding: '8px 16px 14px' }}>

      {/* ════════════════════════════════════════════════════════
           FULL-WIDTH STAT TABLE: Season / 14 Day Rolling / Projected
           ════════════════════════════════════════════════════════ */}
      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <div style={styles.panelTitle}>{currentSeason} Season</div>
          <div style={styles.panelSub}>{gamesPlayed} GP</div>
        </div>
        {byEngine && (
          <ProjectionEngineSwitcher engine={projectionEngine} onSelect={selectEngine}
                                    disagreement={enginesDisagree} allAgree={allEnginesAgree} isPitcher={isPitcher} />
        )}
        {/* DD-B4 (Valdez 699013, 2026-07-14): the ROS OPS headline is ALWAYS P9's published
            forward number (one product, one number). When a user opens a single-engine
            breakdown, the OTHER projection cells show that engine's view — named here so the
            single-engine numbers are never mistaken for the published read. */}
        {byEngine && projectionEngine && projectionEngine !== 'consensus' && (
          <div style={{ fontSize: 12, color: dynastyTheme.tokens.colors.warning, margin: '2px 0 6px' }}>
            Single-engine breakdown ({engineLabel}) — your saved preference drives the per-stat
            projections below. The Rest-of-Season OPS stays the published forward number (matches
            the Prophet 9 page).
          </div>
        )}
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.thFirst }}></th>
              {columns.map(col => (
                <th key={col.key} style={styles.th}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Season Row — white */}
            <tr>
              <td style={{ ...styles.td, ...styles.tdFirst, color: styles.seasonColor }}>Season</td>
              {columns.map(col => (
                <td key={col.key} style={{ ...styles.td, color: styles.seasonColor }}>{getSeasonVal(col)}</td>
              ))}
            </tr>
            {/* 14 Day Rolling — yellow */}
            <tr>
              <td style={{ ...styles.td, ...styles.tdFirst, color: styles.l14Color }}>14 Day Rolling</td>
              {columns.map(col => (
                <td key={col.key} style={{ ...styles.td, color: styles.l14Color }}>{getRollingVal(col)}</td>
              ))}
            </tr>
            {/* Projected — Season Total (whole-season, engine-aware) — light blue, bold */}
            <tr>
              <td style={{ ...styles.td, ...styles.tdFirst, color: styles.projectedColor, fontWeight: 600, borderBottom: 'none' }}>Season Total (proj{engineSuffix})</td>
              {Object.keys(projWholeSeason).length === 0 ? (
                <td colSpan={columns.length} style={{ ...styles.td, color: dynastyTheme.tokens.colors.neutral[600], fontStyle: 'italic', fontWeight: 400, borderBottom: 'none', textAlign: 'center' }}>
                  Awaiting projection data
                </td>
              ) : (
                columns.map(col => (
                  <td key={col.key} style={{ ...styles.td, color: styles.projectedColor, fontWeight: 600, borderBottom: 'none' }}>{getProjectedVal(col)}{agreeMark(col)}</td>
                ))
              )}
            </tr>
            {/* Rest of Season (Fork B) — only when whole-season exists; Rule 9: when projRos
                is absent we show an explicit unavailable marker, never whole-season numbers. */}
            {Object.keys(projWholeSeason).length > 0 && (
              <tr>
                <td style={{ ...styles.td, ...styles.tdFirst, color: styles.projectedColor, fontWeight: 600 }}>Rest of Season (proj{engineSuffix})</td>
                {!projRos ? (
                  <td colSpan={columns.length} style={{ ...styles.td, color: dynastyTheme.tokens.colors.warning, fontStyle: 'italic', fontWeight: 400, textAlign: 'center' }}>
                    Rest-of-season projection unavailable
                  </td>
                ) : (
                  columns.map(col => (
                    <td key={col.key} style={{ ...styles.td, color: styles.projectedColor, fontWeight: 600 }}>{getRosVal(col)}{agreeMark(col)}</td>
                  ))
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Chunk 31 — v3 product surfaces: IL risk, anomaly flag, pattern comparables */}
      <ProjectionInsights playerId={playerId} isPitcher={isPitcher} target={isPitcher ? 'ERA' : 'HR'} />

      {/* ════════════════════════════════════════════════════════
           TRIPTYCH: Rate Profile | MLB Rankings | League Rankings
           ════════════════════════════════════════════════════════ */}
      <div style={styles.triptych}>

        {/* ─── Rate Profile ─── */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div style={styles.panelTitle}>Rate Profile{statsSeasonYear ? ` (${statsSeasonYear})` : ''}</div>
            <div style={styles.panelSub}>vs MLB avg</div>
          </div>
          {hasRateStats ? (
            <div style={styles.rateList}>
              {rateStats.map((stat, i) => (
                <div key={i} style={styles.rateItem}>
                  <span style={styles.rateLabel}>{stat.label}</span>
                  <div style={styles.rateTrack}>
                    <div style={{
                      ...styles.rateFill,
                      width: `${Math.min(stat.pct, 100)}%`,
                      background: getRateBarColor(
                        stat.rawValue ?? parseFloat(stat.value),
                        stat.avg,
                        stat.higherIsBetter
                      ),
                    }} />
                    <div style={{ ...styles.rateAvgMarker, left: `${Math.min(stat.avgPct, 100)}%` }} />
                  </div>
                  <span style={styles.rateVal}>
                    {stat.value}
                    <span style={styles.rateCtx}>avg {typeof stat.avg === 'number' && stat.avg < 1 ? stat.avg.toFixed(3) : stat.avg}{stat.label.includes('%') ? '%' : ''}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ ...styles.rateList, color: '#9ca3af', fontSize: 12 }}>
              No {isPitcher ? 'innings pitched' : 'plate appearances'} this season — no rate profile yet.
            </div>
          )}
        </div>

        {/* ─── MLB Rankings ─── */}
        <div style={styles.panel}>
          <div style={styles.panelHeaderStack}>
            <div style={styles.panelTitle}>MLB Rankings ({mlbRankingsYear})</div>
            <div style={styles.panelControlStrip}>
              {/* Chunk 3.11.a.1: Selectable per-year position buttons. "All" plus one
                  button per position the player has cache entries under for the
                  selected year. Selection state is mlbScope ('all' or position_code).
                  If a previously-selected position isn't in the new year's set
                  (e.g., after year toggle), the effective scope falls back to 'all'
                  for display — no cross-position fallback (Rule 9).
                  Phase 1.2 (2026-05-20): position buttons + year + scope toggles
                  share one wrapping flex strip — uniform layout across both cards. */}
              {(() => {
                const mlbPositionsList = (mlbRankings?.categories?.position || []).map(r => r.position_code);
                const effectiveScope = (mlbScope === 'all' || mlbPositionsList.includes(mlbScope)) ? mlbScope : 'all';
                return (
                  <div style={{ display: 'inline-flex', gap: 2, flexWrap: 'wrap', rowGap: 2 }}>
                    <button onClick={() => setMlbScope('all')}
                      style={{ ...TOGGLE_BTN,
                        backgroundColor: effectiveScope === 'all' ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700],
                        color: effectiveScope === 'all' ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
                    >All</button>
                    {mlbPositionsList.map(pos => (
                      <button key={pos} onClick={() => setMlbScope(pos)}
                        style={{ ...TOGGLE_BTN,
                          backgroundColor: effectiveScope === pos ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700],
                          color: effectiveScope === pos ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
                      >{pos}</button>
                    ))}
                  </div>
                );
              })()}
              <YearToggle active={mlbRankingsYear} onToggle={setMlbRankingsYear} currentSeason={currentSeason} />
              {/* Phase 1.2 fix (2026-05-20):
                  - Relabel "League" → "Scoring" — the toggle filters to the
                    league's scored categories, not a "scope" choice.
                  - Filter now applies to BOTH rankings cards (was wired to
                    MLB card only; League card render block at the bottom of
                    this section also reads catFilter). State renamed
                    `mlbCatFilter` → `catFilter` since it's no longer MLB-only. */}
              <ScopeToggle active={catFilter} onToggle={setCatFilter} leftKey="league" leftLabel="Scoring" rightKey="all" positionLabel="All Cats" />
            </div>
          </div>
          {(() => {
            const mlbPositionsList = (mlbRankings?.categories?.position || []).map(r => r.position_code);
            const effectiveScope = (mlbScope === 'all' || mlbPositionsList.includes(mlbScope)) ? mlbScope : 'all';
            const mlbSrc = effectiveScope === 'all'
              ? mlbRankings?.categories?.mlb
              : (mlbRankings?.categories?.position || []).find(r => r.position_code === effectiveScope);
            const catRanks = mlbSrc?.category_ranks;
            const total = mlbSrc?.total_players;
            const overallRank = effectiveScope === 'all' ? mlbRankings?.overall?.mlb?.rank : mlbSrc?.rank;
            const overallTotal = effectiveScope === 'all' ? mlbRankings?.overall?.mlb?.total_players : total;

            // Chunk 3.11.a #1b: distinguish loading / no-cache / no-scope states.
            if (mlbRankingsLoading) {
              return <div style={{ padding: 14, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[600], fontSize: 13 }}>Loading {mlbRankingsYear}…</div>;
            }
            if (!mlbRankings) {
              return <div style={{ padding: 14, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[600], fontSize: 13 }}>No rankings cached for {mlbRankingsYear}</div>;
            }
            if (!catRanks) {
              return <div style={{ padding: 14, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[600], fontSize: 13 }}>No {effectiveScope === 'all' ? '' : `${effectiveScope} `}MLB rankings for {mlbRankingsYear}</div>;
            }

            // Filter categories — uses module-level CATEGORY_TO_KEY.
            let entries = Object.entries(catRanks);
            if (catFilter === 'league' && leagueScoring) {
              const lcats = (isPitcher ? leagueScoring.pitching : leagueScoring.hitting) ?? [];
              const allowedKeys = lcats.map(c => CATEGORY_TO_KEY[c.toLowerCase()] ?? c.toLowerCase());
              entries = entries.filter(([key]) => allowedKeys.includes(key.toLowerCase()));
            }

            return (
              <>
                {overallRank && (
                  <div style={styles.rankOverall}>
                    <div style={styles.roText}>#{overallRank} out of {overallTotal} {effectiveScope === 'all' ? 'Overall' : effectiveScope}</div>
                    <div style={styles.roSub}>Composite z-score</div>
                  </div>
                )}
                <div style={styles.rankList}>
                  {entries.slice(0, 8).map(([stat, rank]) => (
                    <div key={stat} style={styles.rlRow}>
                      <span style={styles.rlCat}>{RANK_DISPLAY_NAMES[stat] || stat}</span>
                      <span style={styles.rlVal}>#{rank} / {total}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>

        {/* ─── League Rankings ─── */}
        <div style={styles.panel}>
          <div style={styles.panelHeaderStack}>
            <div style={styles.panelTitle}>League Rankings ({leagueRankingsYear})</div>
            <div style={styles.panelControlStrip}>
              {/* Chunk 3.11.a.1: Selectable per-year position buttons (League side).
                  Phase 1.2 (2026-05-20): same wrapping-strip layout as MLB card. */}
              {(() => {
                const leaguePositionsList = (leagueRankings?.categories?.position_league || []).map(r => r.position_code);
                const effectiveScope = (leagueScope === 'all' || leaguePositionsList.includes(leagueScope)) ? leagueScope : 'all';
                return (
                  <div style={{ display: 'inline-flex', gap: 2, flexWrap: 'wrap', rowGap: 2 }}>
                    <button onClick={() => setLeagueScope('all')}
                      style={{ ...TOGGLE_BTN,
                        backgroundColor: effectiveScope === 'all' ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700],
                        color: effectiveScope === 'all' ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
                    >All</button>
                    {leaguePositionsList.map(pos => (
                      <button key={pos} onClick={() => setLeagueScope(pos)}
                        style={{ ...TOGGLE_BTN,
                          backgroundColor: effectiveScope === pos ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700],
                          color: effectiveScope === pos ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.neutral[400] }}
                      >{pos}</button>
                    ))}
                  </div>
                );
              })()}
              <YearToggle active={leagueRankingsYear} onToggle={setLeagueRankingsYear} currentSeason={currentSeason} />
            </div>
          </div>
          {(() => {
            const leaguePositionsList = (leagueRankings?.categories?.position_league || []).map(r => r.position_code);
            const effectiveScope = (leagueScope === 'all' || leaguePositionsList.includes(leagueScope)) ? leagueScope : 'all';
            const leagueSrc = effectiveScope === 'all'
              ? leagueRankings?.categories?.league
              : (leagueRankings?.categories?.position_league || []).find(r => r.position_code === effectiveScope);
            // Option C (2026-05-20): when the selected position scope's record
            // is a free-agent PROJECTION (no real rostered row), the per-cat
            // display shows z-scores (from category_z_scores), not ranks —
            // the projection has no per-category slot, only composite.
            // Real records continue to show ranks from category_ranks.
            const isProjected = leagueSrc?.projected === true;
            const catRanks = leagueSrc?.category_ranks;
            const catZ = leagueSrc?.category_z_scores;
            const total = leagueSrc?.total_players;
            const overallRank = effectiveScope === 'all' ? leagueRankings?.overall?.league?.rank : leagueSrc?.rank;
            const overallTotal = effectiveScope === 'all' ? leagueRankings?.overall?.league?.total_players : total;
            const rosteredCohort = leagueSrc?.rostered_cohort_size;

            // Chunk 3.11.a #1b: distinguish loading / no-cache / no-scope states.
            if (leagueRankingsLoading) {
              return <div style={{ padding: 14, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[600], fontSize: 13 }}>Loading {leagueRankingsYear}…</div>;
            }
            if (!leagueRankings) {
              return <div style={{ padding: 14, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[600], fontSize: 13 }}>No rankings cached for {leagueRankingsYear}</div>;
            }
            // Pick the per-row source: projected shows z-scores, real shows ranks.
            const perRowSource = isProjected ? catZ : catRanks;
            if (!perRowSource || Object.keys(perRowSource).length === 0) {
              return <div style={{ padding: 14, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[600], fontSize: 13 }}>No {effectiveScope === 'all' ? '' : `${effectiveScope} `}League rankings for {leagueRankingsYear}</div>;
            }

            // Phase 1.2 fix (2026-05-20): apply the same Scoring/All Cats
            // filter as the MLB card (shared `catFilter` state, module-level
            // CATEGORY_TO_KEY). Previously the League card just sliced the
            // first 8 entries unfiltered — the toggle visibly did nothing
            // on this card. Now both cards respond to the same toggle.
            let entries = Object.entries(perRowSource);
            if (catFilter === 'league' && leagueScoring) {
              const lcats = (isPitcher ? leagueScoring.pitching : leagueScoring.hitting) ?? [];
              const allowedKeys = lcats.map(c => CATEGORY_TO_KEY[c.toLowerCase()] ?? c.toLowerCase());
              entries = entries.filter(([key]) => allowedKeys.includes(key.toLowerCase()));
            }

            return (
              <>
                {overallRank && (
                  <div style={styles.rankOverall}>
                    <div style={{
                      ...styles.roText,
                      fontStyle: isProjected ? 'italic' : 'normal',
                      color: isProjected ? dynastyTheme.tokens.colors.primary : undefined,
                    }}>
                      {isProjected
                        ? `Projected: ~#${overallRank} of ${(rosteredCohort ?? Math.max(0, (overallTotal || 1) - 1))}+1 ${effectiveScope === 'all' ? 'Overall' : effectiveScope} (if rostered)`
                        : `#${overallRank} out of ${overallTotal} ${effectiveScope === 'all' ? 'Overall' : effectiveScope}`}
                    </div>
                    <div style={styles.roSub}>
                      {isProjected ? 'Composite z-score · free-agent projection' : 'Composite z-score'}
                    </div>
                  </div>
                )}
                <div style={{
                  ...styles.rankList,
                  ...(isProjected ? { fontStyle: 'italic' } : {}),
                }}>
                  {entries.slice(0, 8).map(([stat, val]) => (
                    <div key={stat} style={styles.rlRow}>
                      <span style={styles.rlCat}>{RANK_DISPLAY_NAMES[stat] || stat}</span>
                      <span style={styles.rlVal}>
                        {isProjected
                          ? (typeof val === 'number' ? `z: ${val >= 0 ? '+' : ''}${val.toFixed(2)}` : '—')
                          : `#${val} / ${total}`}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
           MONTHLY — Full-width thin strip below triptych
           ════════════════════════════════════════════════════════ */}
      {monthly && Array.isArray(monthly) && monthly.length > 0 && !monthly[0]?.message && (
        <div style={{ ...styles.panel, ...styles.monthlyStrip }}>
          <div style={styles.panelHeader}>
            <div style={styles.panelTitle}>Monthly</div>
            <div style={styles.panelSub}>{isPitcher ? 'ERA' : 'AVG'}</div>
          </div>
          <div style={styles.monthlyGrid}>
            {MONTH_LABELS.map((label, idx) => {
              const monthData = monthly[idx];
              const statVal = monthData
                ? (isPitcher ? monthData.era : monthData.avg)
                : null;
              const heat = getMonthlyStyle(statVal, isPitcher);

              return (
                <div key={label} style={{
                  borderRadius: 4,
                  padding: '4px 2px',
                  textAlign: 'center',
                  background: heat.background,
                  border: heat.border,
                }}>
                  <div style={styles.moName}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: heat.color }}>
                    {statVal != null
                      ? (isPitcher ? formatERA(statVal) : formatBattingAvg(statVal))
                      : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(PlayerOverviewTab);
